/** 开户姓名：中文用 fullName；英文分项拼 pinyinName，供二要素、签名、银行户名继续使用。 */

export type NameParts = {
  lastNameCn?: string;
  firstNameCn?: string;
  firstNameEn?: string;
  lastNameEn?: string;
  fullName?: string;
  pinyinName?: string;
};

export const trimName = (value?: string | null) => (value || '').trim();

/** 中文：姓+名，无空格（仅历史数据合并用） */
export const joinFullName = (lastNameCn?: string, firstNameCn?: string) =>
  `${trimName(lastNameCn)}${trimName(firstNameCn)}`;

/** 英文：名 + 空格 + 姓；没有姓时只保留名 */
export const joinPinyinName = (firstNameEn?: string, lastNameEn?: string) => {
  const given = trimName(firstNameEn);
  const family = trimName(lastNameEn);
  return family ? `${given} ${family}`.trim() : given;
};

export const hasAnyEnNamePart = (parts: NameParts) =>
  !!(trimName(parts.firstNameEn) || trimName(parts.lastNameEn));

/** 有中文名字，或仅有英文名字。英文姓氏可空。 */
export const hasRequiredName = (parts: NameParts) => {
  const cnOk = !!trimName(parts.fullName);
  const enOk = !!trimName(parts.firstNameEn);
  return cnOk || enOk;
};

/** 用户改英文分项后重算 pinyinName。 */
export const applyJoinedNames = (form: NameParts) => {
  form.pinyinName = joinPinyinName(form.firstNameEn, form.lastNameEn);
  return form;
};

/**
 * 接口回填。优先 fullName / pinyinName；仅有历史 lastNameCn+firstNameCn 时合并为 fullName。
 */
export const readNameParts = (base: NameParts | null | undefined): Required<NameParts> => {
  const firstNameEn = trimName(base?.firstNameEn);
  const lastNameEn = trimName(base?.lastNameEn);
  const lastNameCn = trimName(base?.lastNameCn);
  const firstNameCn = trimName(base?.firstNameCn);
  let fullName = trimName(base?.fullName);
  if (!fullName && (lastNameCn || firstNameCn)) {
    fullName = joinFullName(lastNameCn, firstNameCn);
  }
  let pinyinName = trimName(base?.pinyinName);
  if (!pinyinName && hasAnyEnNamePart({ firstNameEn, lastNameEn })) {
    pinyinName = joinPinyinName(firstNameEn, lastNameEn);
  }
  return {
    lastNameCn: '',
    firstNameCn: '',
    firstNameEn,
    lastNameEn,
    fullName,
    pinyinName,
  };
};

/** OCR：整名或分项；有 fullName 时不再依赖中文姓/名拆分。 */
export const fillOcrNameParts = (form: NameParts, ocr: object | null | undefined) => {
  if (!ocr) return;
  const source = ocr as Record<string, unknown>;
  let hit = false;

  for (const key of ['fullName', 'name'] as const) {
    const raw = source[key];
    if (raw == null) continue;
    const text = String(raw).trim();
    if (!text) continue;
    form.fullName = text;
    hit = true;
    break;
  }

  const enKeys = ['firstNameEn', 'lastNameEn'] as const;
  for (const key of enKeys) {
    const raw = source[key];
    if (raw == null) continue;
    const text = String(raw).trim();
    if (!text) continue;
    form[key] = text;
    hit = true;
  }

  if (!form.fullName) {
    const cnFromParts = joinFullName(
      String(source.lastNameCn ?? '').trim(),
      String(source.firstNameCn ?? '').trim(),
    );
    if (cnFromParts) {
      form.fullName = cnFromParts;
      hit = true;
    }
  }

  if (hit) applyJoinedNames(form);
};

const dash = (value: string) => value || '-';

/** 展示：中文名字一行 + 英文名字/英文姓氏。 */
export const namePartDisplayValues = (parts: NameParts) => {
  const fullName = trimName(parts.fullName);
  const joinedLegacy = joinFullName(parts.lastNameCn, parts.firstNameCn);
  const cnDisplay = fullName || joinedLegacy;
  const enGiven = trimName(parts.firstNameEn);
  const enFamily = trimName(parts.lastNameEn);
  const pinyin = trimName(parts.pinyinName);
  const noEnParts = !enGiven && !enFamily;
  return {
    fullName: dash(cnDisplay),
    firstNameEn: dash(enGiven || (noEnParts ? pinyin : enGiven)),
    lastNameEn: dash(enFamily),
  };
};
